import {
  ErrorModal,
  HelmetDexteel,
  useHasPermission,
  useUserContext,
} from "@dexteel/mesf-core";
import { Refresh as RefreshIcon, Add as AddIcon } from "@mui/icons-material";
import { Typography, IconButton, Tooltip, Box, Button } from "@mui/material";
import { Group, Panel } from "react-resizable-panels";
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { LazyBackDropLoading } from "@/controls/LazyLoading";
import { RoofTreePanel } from "./components/RoofTreePanel";
import {
  RoofMapPanel,
  RoofSvgThumbnail,
  type RoofData,
} from "./components/map-view";
import {
  CorrectDatesModal,
  DeleteConfirmModal,
  EditPartModal,
  InstallPartModal,
  NewSparePartModal,
  NoPermissionModal,
  NotesModal,
  SetInitialMetricModal,
  SetEquipmentStatusModal,
} from "../../shared/components/modals";
import {
  HistoryPanel,
  MetricsPanel,
  DetailsPanel,
  ViewToggle,
  type ViewMode,
} from "../../shared/components";
import {
  getTaskMetadata,
  getPartDetails as getPartDetailsRoof,
} from "./repositories/RoofRepository";
import { useResizablePanels } from "../../../sy/hooks/useResizablePanels";
import { ResizablePanelSeparator } from "../../../sy/components/ResizablePanelSeparator";
import { useVerticalDragResize } from "../../../sy/hooks/useVerticalDragResize";
import { VerticalDragSeparator } from "../../../sy/components/VerticalDragSeparator";
import { getPartDetailsParams } from "../../shared/utils/getPartDetailsParams";
import {
  usePartDetailsQuery,
  type GetPartDetailsFn,
} from "../../shared/hooks/queries";
import type { PartInfo } from "../../shared/models";

// Cast the roof-specific getPartDetails to the shared generic type.
// The runtime shapes are compatible — this cast bridges the typed rows vs Record<string, unknown>.
const getPartDetails = getPartDetailsRoof as unknown as GetPartDetailsFn;

/** Extract error message from an unknown error (TanStack Query default error type). */
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return "An unexpected error occurred";
};
import type {
  PartDetailsMasterAPI,
  PartDetailsHistoryAPI,
  RoofTreeRow,
} from "./models";
import {
  PART_IDS,
  isSpareContainer,
  transformHistoryAPIToEntries,
} from "./models";
import { useRoofPartsTreeQuery } from "./hooks/queries";
import {
  useSetRoofOnlineMutation,
  useSetRoofOfflineMutation,
  useInstallRoofPartMutation,
  useInstallExistingPartMutation,
  useUninstallRoofPartMutation,
  useRepairRoofPartMutation,
  useUpsertRoofPartDetailsMutation,
  useCreateSparePartMutation,
  useDeleteSparePartMutation,
} from "./hooks/mutations";
import {
  useSetInitialMetricValueMutation,
  useUpdateTaskTimestampMutation,
} from "../../shared/hooks/mutations";
import { useSetEquipmentOnlineOffline } from "../../shared/hooks/useSetEquipmentOnlineOffline";
export const RoofPanelPage = () => {
  const { state: userState } = useUserContext();
  const hasPermission = useHasPermission();
  const canSetOnline = hasPermission("RBD.EQPRoofSetOnline");
  const canSetOffline = hasPermission("RBD.EQPRoofSetOffline");
  const canEditTimestamp = hasPermission("EQP.UpsertTask");

  // Resizable panels (horizontal — tree vs details)
  const { groupRef, defaultLayout, onLayoutChanged, resetLayout } =
    useResizablePanels({
      groupId: "roof-panels",
      defaultLayout: { tree: 50, details: 50 },
    });

  // Vertical drag-to-resize for details+metrics vs history (content-adaptive)
  const { dragHeight, containerRef, handleDragStart, resetDragHeight } =
    useVerticalDragResize();

  // ==========================================================================
  // Query Hooks (replace loadData, loadHistory, loadMetrics)
  // ==========================================================================
  const {
    data: treeData = [],
    isLoading: isLoadingTree,
    refetch: refetchTree,
  } = useRoofPartsTreeQuery();

  // Keep a ref in sync for callbacks that need current tree data
  const treeDataRef = useRef<RoofTreeRow[]>([]);
  useEffect(() => {
    treeDataRef.current = treeData;
  }, [treeData]);

  // ==========================================================================
  // Mutation Hooks
  // ==========================================================================
  const setOnlineMutation = useSetRoofOnlineMutation();
  const setOfflineMutation = useSetRoofOfflineMutation();
  const installPartMutation = useInstallRoofPartMutation();
  const installExistingMutation = useInstallExistingPartMutation();
  const uninstallPartMutation = useUninstallRoofPartMutation();
  const repairPartMutation = useRepairRoofPartMutation();
  const upsertDetailsMutation = useUpsertRoofPartDetailsMutation();
  const createSpareMutation = useCreateSparePartMutation();
  const deleteSpareMutation = useDeleteSparePartMutation();
  const setInitialMetricMutation = useSetInitialMetricValueMutation([
    ["roof-tree"],
    ["roof-details"],
  ]);
  const updateTimestampMutation = useUpdateTaskTimestampMutation([
    ["roof-tree"],
    ["roof-details"],
  ]);

  const {
    handleSetOnline: executeSetOnline,
    handleSetOffline: executeSetOffline,
  } = useSetEquipmentOnlineOffline({
    setOnlineMutation,
    setOfflineMutation,
    findOccupant: (treeData, _targetRow) =>
      treeData.find(
        (row) =>
          row.rowType === "equipment" &&
          row.Status === "Online" &&
          row.EquipmentId !== _targetRow.EquipmentId,
      ) ?? null,
    buildOnlineParams: (targetRow, timestamp) => ({
      roofPartId: targetRow.EquipmentId,
      parentPartId: PART_IDS.EAF_ONLINE,
      timestamp,
    }),
    buildOfflineParams: (occupant, timestamp) => ({
      roofPartId: occupant.EquipmentId,
      timestamp,
    }),
  });

  const isMutating =
    setOnlineMutation.isPending ||
    setOfflineMutation.isPending ||
    installPartMutation.isPending ||
    installExistingMutation.isPending ||
    uninstallPartMutation.isPending ||
    repairPartMutation.isPending ||
    upsertDetailsMutation.isPending ||
    createSpareMutation.isPending ||
    deleteSpareMutation.isPending;

  // ==========================================================================
  // UI State
  // ==========================================================================
  const [selectedRow, setSelectedRow] = useState<RoofTreeRow | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("tree");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Modal state for context menu actions
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [newSparePartModalOpen, setNewSparePartModalOpen] = useState(false);
  const [editPartModalOpen, setEditPartModalOpen] = useState(false);
  const [setInitialMetricModalOpen, setSetInitialMetricModalOpen] =
    useState(false);
  const [correctDatesModalOpen, setCorrectDatesModalOpen] = useState(false);
  const [correctDatesPartId, setCorrectDatesPartId] = useState<number | null>(
    null,
  );
  const [correctDatesPartName, setCorrectDatesPartName] = useState<
    string | null
  >(null);
  const [modalAction, setModalAction] = useState<"uninstall" | "repair" | null>(
    null,
  );
  const [actionRow, setActionRow] = useState<RoofTreeRow | null>(null);

  // Roof status modal state (for online/offline confirmation)
  const [roofStatusModalOpen, setRoofStatusModalOpen] = useState(false);
  const [roofStatusModalAction, setRoofStatusModalAction] = useState<
    "online" | "offline"
  >("online");
  const [roofStatusModalEquipmentId, setRoofStatusModalEquipmentId] = useState<
    number | null
  >(null);

  // Delete spare part modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [partToDelete, setPartToDelete] = useState<RoofTreeRow | null>(null);

  // No permission modal state
  const [noPermissionModalOpen, setNoPermissionModalOpen] = useState(false);
  const [noPermissionAction, setNoPermissionAction] = useState<string | null>(
    null,
  );

  // ==========================================================================
  // Derived Data from Query Cache (for modals that need master/metrics data)
  // ==========================================================================
  // The page calls usePartDetailsQuery for the selected equipment row.
  // TanStack Query deduplicates — MetricsPanel and HistoryPanel use the SAME cache entry.
  const partDetailsParams = selectedRow
    ? getPartDetailsParams(selectedRow)
    : null;

  // Page-level query for master data (deduped with MetricsPanel/HistoryPanel)
  const partDetailsQuery = usePartDetailsQuery(
    "roof",
    partDetailsParams,
    getPartDetails,
  );

  // Extract master data from the query result (for MetricsPanel uninstalledOn)
  const selectedEquipmentMasterData: PartDetailsMasterAPI | null =
    selectedRow?.rowType === "equipment" && partDetailsQuery.data
      ? ((partDetailsQuery.data.tables[0]
          ?.rows[0] as unknown as PartDetailsMasterAPI) ?? null)
      : null;

  // Extract metrics from the query result (for SetInitialMetricModal)
  const metrics = (partDetailsQuery.data?.tables[1]?.rows ?? []) as unknown as {
    MetricTypeId: number;
    PartialValue: number | null;
  }[];

  // Track the online roof's installation date (for stale end-campaign detection)
  // Derived from tree data + query cache: find the online roof, look up its master data
  const onlineRoof = treeData.find(
    (row) => row.rowType === "equipment" && row.Status === "Online",
  );
  const onlineRoofParams = onlineRoof ? getPartDetailsParams(onlineRoof) : null;
  const onlineRoofDetailsQuery = usePartDetailsQuery(
    "roof",
    onlineRoofParams,
    getPartDetails,
  );
  const onlineRoofMaster = onlineRoofDetailsQuery.data?.tables[0]
    ?.rows[0] as unknown as PartDetailsMasterAPI | undefined;
  const onlineRoofInstalledOn = onlineRoofMaster?.InstalledOn
    ? new Date(onlineRoofMaster.InstalledOn)
    : null;

  // ==========================================================================
  // Sync selected row with tree data after mutations/refetches
  // ==========================================================================
  useEffect(() => {
    if (treeData.length === 0) return;

    setSelectedRow((current) => {
      if (current) {
        // Keep selection in sync with updated tree data
        return treeData.find((row) => row.id === current.id) ?? null;
      }
      // Initial load: pre-select the online roof
      const onlineRoofRow = treeData.find(
        (row) => row.rowType === "equipment" && row.Status === "Online",
      );
      return onlineRoofRow ?? null;
    });
  }, [treeData]);

  // Reset vertical drag height when selection changes (re-adapt to content)
  useEffect(() => {
    resetDragHeight();
  }, [selectedRow, resetDragHeight]);

  // Handlers
  const handleRowSelection = (row: RoofTreeRow | null) => {
    setSelectedRow(row);
  };

  // Show no permission modal
  const showNoPermissionModal = (actionName: string) => {
    setNoPermissionAction(actionName);
    setNoPermissionModalOpen(true);
  };

  // Open confirmation modal for setting roof online (from tree panel - receives row)
  const handleSetOnlineAction = (row: RoofTreeRow) => {
    if (!canSetOnline) {
      showNoPermissionModal("set roof online");
      return;
    }
    setRoofStatusModalEquipmentId(row.EquipmentId);
    setRoofStatusModalAction("online");
    setRoofStatusModalOpen(true);
  };

  // Open confirmation modal for setting roof offline (from tree panel - receives row)
  const handleSetOfflineAction = (row: RoofTreeRow) => {
    if (!canSetOffline) {
      showNoPermissionModal("set roof offline");
      return;
    }
    setRoofStatusModalEquipmentId(row.EquipmentId);
    setRoofStatusModalAction("offline");
    setRoofStatusModalOpen(true);
  };

  // Handler for map panel - sets online from roof PartId
  const handleSetOnlineFromMap = async (roofPartId: number) => {
    const row = treeDataRef.current.find(
      (r) => r.EquipmentId === roofPartId && r.rowType === "equipment",
    );
    if (row) {
      handleSetOnlineAction(row);
    }
  };

  // Handler for map panel - sets offline from roof PartId
  const handleSetOfflineFromMap = async (roofPartId: number) => {
    const row = treeDataRef.current.find(
      (r) => r.EquipmentId === roofPartId && r.rowType === "equipment",
    );
    if (row) {
      handleSetOfflineAction(row);
    }
  };

  // Handle confirmation from SetRoofStatusModal
  const handleRoofStatusConfirm = async (
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    if (!roofStatusModalEquipmentId) return;

    const equipment = treeDataRef.current.find(
      (row) =>
        row.rowType === "equipment" &&
        row.EquipmentId === roofStatusModalEquipmentId,
    );
    if (!equipment) return;

    try {
      if (roofStatusModalAction === "online") {
        await executeSetOnline(treeDataRef.current, equipment, timestamp);
        setNotificationMessage(`${equipment.EquipmentName} is now Online`);
      } else {
        await executeSetOffline(equipment, timestamp);
        setNotificationMessage(`${equipment.EquipmentName} is now Offline`);
      }
      setRoofStatusModalOpen(false);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "An error occurred",
      );
    }
  };

  // Close roof status modal
  const handleCloseRoofStatusModal = () => {
    setRoofStatusModalOpen(false);
    setRoofStatusModalEquipmentId(null);
  };

  const handleInstallPart = (
    equipmentId: number,
    positionId: number,
    partTypeId: number,
    partInfo: PartInfo,
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    installPartMutation.mutate(
      {
        roofPartId: equipmentId,
        positionId,
        partTypeId,
        partInfo,
        comments: notes,
        operatorNumber,
        timestamp,
      },
      {
        onSuccess: () => {
          setNotificationMessage("Part installed successfully");
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleUninstallPart = (
    l2PartId: number,
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    uninstallPartMutation.mutate(
      {
        partId: l2PartId,
        comments: notes,
        operatorNumber,
        timestamp,
      },
      {
        onSuccess: () => {
          setNotificationMessage("Part uninstalled successfully");
        },
        onError: (error) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleRepairPart = (
    l2PartId: number,
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    repairPartMutation.mutate(
      {
        partId: l2PartId,
        comments: notes,
        operatorNumber,
        timestamp,
      },
      {
        onSuccess: () => {
          setNotificationMessage("Repair logged successfully");
        },
        onError: (error) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  // Handler for installing an existing spare part
  const handleInstallExistingPart = (
    existingPartId: number,
    roofPartId: number,
    positionId: number,
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    installExistingMutation.mutate(
      {
        existingPartId,
        roofPartId,
        positionId,
        comments: notes,
        operatorNumber,
        timestamp,
      },
      {
        onSuccess: () => {
          setNotificationMessage("Part installed successfully");
        },
        onError: (error) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  // Handler for install action
  const handleInstallAction = (row: RoofTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setInstallModalOpen(true);
  };

  // Handler for uninstall action
  const handleUninstallAction = (row: RoofTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setModalAction("uninstall");
    setNotesModalOpen(true);
  };

  // Handler for repair action
  const handleRepairAction = (row: RoofTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setModalAction("repair");
    setNotesModalOpen(true);
  };

  // Handler for new spare part action
  const handleNewSparePartAction = (row: RoofTreeRow) => {
    setActionRow(row);
    setNewSparePartModalOpen(true);
  };

  // Handler for edit part action
  const handleEditPartAction = (row: RoofTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setEditPartModalOpen(true);
  };

  // Handler for set initial metric action (from context menu)
  const handleSetInitialMetricAction = (row: RoofTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setSetInitialMetricModalOpen(true);
  };

  // Handler for notes modal confirm (uninstall/repair)
  const handleNotesModalConfirm = (
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    if (!actionRow || !modalAction) return;

    const mutationCallbacks = {
      onSuccess: () => {
        const message =
          modalAction === "uninstall"
            ? "Part uninstalled successfully"
            : "Repair logged successfully";
        setNotificationMessage(message);
        setNotesModalOpen(false);
        setModalAction(null);
        setActionRow(null);
      },
      onError: (error: unknown) => {
        setErrorMessage(getErrorMessage(error));
      },
    };

    if (modalAction === "uninstall" && actionRow.L2PartId != null) {
      uninstallPartMutation.mutate(
        {
          partId: actionRow.L2PartId,
          comments: notes,
          operatorNumber,
          timestamp,
        },
        mutationCallbacks,
      );
    } else if (modalAction === "repair" && actionRow.L2PartId != null) {
      repairPartMutation.mutate(
        {
          partId: actionRow.L2PartId,
          comments: notes,
          operatorNumber,
          timestamp,
        },
        mutationCallbacks,
      );
    }
  };

  // Handler for closing modals
  const handleCloseInstallModal = () => {
    setInstallModalOpen(false);
    setActionRow(null);
  };

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
    setModalAction(null);
    setActionRow(null);
  };

  const handleCloseNewSparePartModal = () => {
    setNewSparePartModalOpen(false);
    setActionRow(null);
  };

  const handleCloseEditPartModal = () => {
    setEditPartModalOpen(false);
    setActionRow(null);
  };

  const handleCloseSetInitialMetricModal = () => {
    setSetInitialMetricModalOpen(false);
    setActionRow(null);
  };

  // Handler for set initial metric modal confirm
  const handleSetInitialMetricConfirm = (
    newPartialValue: number,
    _operatorNumber: number,
    applyToChildren: boolean,
  ) => {
    const partId = applyToChildren
      ? (actionRow?.L2PartId ?? actionRow?.EquipmentId)
      : (actionRow?.L2PartId ??
        actionRow?.EquipmentId ??
        selectedRow?.L2PartId);
    if (!partId) return;

    setInitialMetricMutation.mutate(
      {
        partId,
        newValue: newPartialValue,
        applyToChildren,
        userId: userState.userId,
      },
      {
        onSuccess: (resp: {
          data?: { tables?: { rows?: Record<string, unknown>[] }[] };
        }) => {
          if (applyToChildren) {
            const firstRow = resp.data?.tables?.[0]?.rows?.[0];
            const count =
              (firstRow as { AffectedCount?: number } | undefined)
                ?.AffectedCount ?? 0;
            setNotificationMessage(`Initial metric set for ${count} parts`);
          } else {
            setNotificationMessage("Initial metric set successfully");
          }
          setSetInitialMetricModalOpen(false);
          setActionRow(null);
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleCorrectDatesAction = (row: RoofTreeRow) => {
    if (!canEditTimestamp) {
      showNoPermissionModal("correct dates");
      return;
    }
    const partId = row.rowType === "equipment" ? row.EquipmentId : row.L2PartId;
    if (!partId) return;
    setCorrectDatesPartId(partId);
    setCorrectDatesPartName(
      row.rowType === "equipment"
        ? (row.EquipmentName ?? null)
        : (row.L2PartName ?? row.PositionName ?? null),
    );
    setCorrectDatesModalOpen(true);
  };

  const handleCorrectDatesSave = async (
    taskId: number,
    newTimestamp: Date,
  ): Promise<boolean> => {
    try {
      await updateTimestampMutation.mutateAsync({
        taskId,
        newTimestamp,
      });
      setNotificationMessage("Timestamp updated successfully");
      return true;
    } catch (error: unknown) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to update timestamp",
      );
      return false;
    }
  };

  const handleCloseCorrectDatesModal = () => {
    setCorrectDatesModalOpen(false);
    setCorrectDatesPartId(null);
    setCorrectDatesPartName(null);
  };

  // Handler for delete spare part action (from context menu)
  const handleDeleteSparePartAction = (row: RoofTreeRow) => {
    setPartToDelete(row);
    setDeleteModalOpen(true);
  };

  // Handler for closing delete modal
  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setPartToDelete(null);
  };

  // Handler for confirming delete spare part
  const handleDeleteSparePartConfirm = () => {
    if (!partToDelete?.L2PartId) return;

    const deletedPartId = partToDelete.L2PartId;
    deleteSpareMutation.mutate(
      { partId: deletedPartId, userId: userState.userId },
      {
        onSuccess: () => {
          setNotificationMessage("Spare part deleted successfully");
          setDeleteModalOpen(false);
          setPartToDelete(null);
          // Clear selection if deleted part was selected
          if (selectedRow?.L2PartId === deletedPartId) {
            setSelectedRow(null);
          }
        },
        onError: (error) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  // Handler for new spare part modal confirm
  const handleNewSparePartConfirm = (partInfo: PartInfo) => {
    if (!actionRow) return;

    const containerId = actionRow.ContainerId ?? actionRow.EquipmentId;

    createSpareMutation.mutate(
      { containerId, partInfo, userId: userState.userId },
      {
        onSuccess: () => {
          setNotificationMessage("Spare part created successfully");
        },
        onError: (error) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  // Handler for edit part modal confirm
  const handleEditPartConfirm = (l2PartId: number, partInfo: PartInfo) => {
    upsertDetailsMutation.mutate(
      { partId: l2PartId, partInfo, comments: null },
      {
        onSuccess: () => {
          setNotificationMessage("Part updated successfully");
        },
        onError: (error) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  // Render function for roof SVG thumbnail in DetailsPanel
  const renderRoofThumbnail = useCallback(
    (row: RoofTreeRow, data?: RoofTreeRow[]): ReactNode => {
      if (!row || !data) return null;

      let roofData: RoofData | null = null;

      if (row.rowType === "equipment") {
        // For equipment (roof), show all locations
        const locations = data.filter(
          (r) => r.rowType === "location" && r.EquipmentId === row.EquipmentId,
        );
        roofData = {
          roofId: row.EquipmentId,
          roofName: row.EquipmentName ?? "",
          isOnline: row.Status === "Online",
          heats: row.Heats ?? 0,
          totalHeats: row.TotalHeats ?? 0,
          locations,
        };
      } else if (row.rowType === "location") {
        // For location, highlight just this position
        const highlightedLocation: RoofTreeRow = {
          ...row,
          HasAssignedPart: true,
        };
        roofData = {
          roofId: row.EquipmentId,
          roofName: row.EquipmentName ?? "",
          isOnline: row.Status === "Online",
          heats: row.Heats ?? 0,
          totalHeats: row.TotalHeats ?? 0,
          locations: [highlightedLocation],
        };
      }

      if (!roofData) return null;

      const size =
        row.rowType === "equipment" ? 200 : row.HasAssignedPart ? 160 : 180;
      return <RoofSvgThumbnail roofData={roofData} size={size} />;
    },
    [],
  );

  return (
    <>
      <HelmetDexteel title="Roof & Delta Panel" />
      <LazyBackDropLoading open={isLoadingTree || isMutating} />

      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Roof & Delta Panel
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ViewToggle view={viewMode} onChange={setViewMode} />
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => refetchTree()}
                disabled={isLoadingTree}
                color="primary"
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Group
          id="roof-panels"
          orientation="horizontal"
          onLayoutChanged={onLayoutChanged}
          groupRef={groupRef}
          defaultLayout={defaultLayout}
          style={{ height: "calc(100vh - 150px)" }}
        >
          <Panel id="tree" minSize={20}>
            {viewMode === "tree" ? (
              <RoofTreePanel
                treeData={treeData}
                selectedRow={selectedRow}
                isLoading={isLoadingTree}
                onRowSelect={handleRowSelection}
                onSetOnline={handleSetOnlineAction}
                onSetOffline={handleSetOfflineAction}
                onInstall={handleInstallAction}
                onUninstall={handleUninstallAction}
                onRepair={handleRepairAction}
                onNewSparePart={handleNewSparePartAction}
                onEditPart={handleEditPartAction}
                onSetInitialMetric={handleSetInitialMetricAction}
                onCorrectDates={handleCorrectDatesAction}
                onDeleteSparePart={handleDeleteSparePartAction}
              />
            ) : (
              <RoofMapPanel
                treeData={treeData}
                selectedRow={selectedRow}
                onSelectRow={handleRowSelection}
                onSetOnline={handleSetOnlineFromMap}
                onSetOffline={handleSetOfflineFromMap}
                onInstall={handleInstallAction}
                onUninstall={handleUninstallAction}
                onRepair={handleRepairAction}
                onNewSparePart={handleNewSparePartAction}
                onEditPart={handleEditPartAction}
                onSetInitialMetric={handleSetInitialMetricAction}
                isLoading={isLoadingTree}
              />
            )}
          </Panel>

          <ResizablePanelSeparator onDoubleClick={resetLayout} />

          <Panel id="details" minSize={25}>
            {viewMode === "map" &&
            (selectedRow?.rowType === "container" ||
              selectedRow?.rowType === "sparesContainer") ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Please select a Part
                </Typography>
              </Box>
            ) : selectedRow?.ContainerId === PART_IDS.EAF_ONLINE ||
              selectedRow?.ContainerId === PART_IDS.EAF_OFFLINE ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Please select a Part
                </Typography>
              </Box>
            ) : selectedRow?.ContainerId === PART_IDS.ROOF_SPARES ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Please select a Roof
                </Typography>
              </Box>
            ) : selectedRow?.ContainerId === PART_IDS.ROOF_PARTS_SPARES ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Please select a Part Container or a Part
                </Typography>
              </Box>
            ) : selectedRow?.rowType === "sparesContainer" &&
              isSpareContainer(selectedRow?.ContainerId) ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                  gap: 2,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Select a part or create a new one
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => handleNewSparePartAction(selectedRow)}
                >
                  New Spare Part
                </Button>
              </Box>
            ) : (
              <Box
                ref={containerRef}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    flex: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    ...(dragHeight != null
                      ? { height: dragHeight, overflow: "auto" }
                      : {}),
                  }}
                >
                  <DetailsPanel
                    treeData={treeData}
                    selectedRow={selectedRow}
                    onInstall={handleInstallAction}
                    onUninstall={handleUninstallAction}
                    onRepair={handleRepairAction}
                    onNewSparePart={handleNewSparePartAction}
                    isSpareContainer={isSpareContainer}
                    renderThumbnail={renderRoofThumbnail}
                  />
                  <MetricsPanel
                    section="roof"
                    partDetailsParams={partDetailsParams}
                    queryFn={getPartDetails}
                    selectedRow={selectedRow}
                    uninstalledOn={
                      selectedEquipmentMasterData?.UninstalledOn
                        ? new Date(selectedEquipmentMasterData.UninstalledOn)
                        : null
                    }
                    onlineEquipmentInstalledOn={onlineRoofInstalledOn}
                    isSpareContainer={isSpareContainer}
                  />
                </Box>

                <VerticalDragSeparator
                  onMouseDown={handleDragStart}
                  onDoubleClick={resetDragHeight}
                />

                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <HistoryPanel
                    section="roof"
                    partDetailsParams={partDetailsParams}
                    queryFn={getPartDetails}
                    getTaskMetadataFn={getTaskMetadata}
                    transformHistory={(rows) =>
                      transformHistoryAPIToEntries(
                        rows as unknown as PartDetailsHistoryAPI[],
                      )
                    }
                    selectedRow={selectedRow}
                    isSpareContainer={isSpareContainer}
                  />
                </Box>
              </Box>
            )}
          </Panel>
        </Group>
      </Box>

      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />

      {/* Install Part Modal (from context menu) */}
      <InstallPartModal
        open={installModalOpen}
        selectedRow={actionRow}
        treeData={treeData}
        onClose={handleCloseInstallModal}
        onInstallNewPart={handleInstallPart}
        onInstallExistingPart={handleInstallExistingPart}
        occupantPartId={actionRow?.L2PartId ?? null}
      />

      {/* Notes Modal for Uninstall/Repair (from context menu) */}
      <NotesModal
        open={notesModalOpen}
        title={modalAction === "uninstall" ? "Uninstall Part" : "Repair Part"}
        confirmLabel={modalAction === "uninstall" ? "Uninstall" : "Repair"}
        confirmColor={modalAction === "uninstall" ? "error" : "warning"}
        partId={actionRow?.L2PartId ?? null}
        action={modalAction ?? "uninstall"}
        onClose={handleCloseNotesModal}
        onConfirm={handleNotesModalConfirm}
      />

      {/* New Spare Part Modal (from context menu) */}
      <NewSparePartModal
        open={newSparePartModalOpen}
        containerName={actionRow?.ContainerName ?? ""}
        onClose={handleCloseNewSparePartModal}
        onConfirm={handleNewSparePartConfirm}
      />

      {/* Edit Part Modal (from context menu) */}
      <EditPartModal
        open={editPartModalOpen}
        selectedRow={actionRow}
        onClose={handleCloseEditPartModal}
        onConfirm={handleEditPartConfirm}
      />

      {/* Set Initial Metric Modal (from context menu) */}
      <SetInitialMetricModal
        open={setInitialMetricModalOpen}
        partName={
          actionRow?.EquipmentName ??
          actionRow?.L2PartName ??
          actionRow?.PositionName ??
          null
        }
        currentPartialValue={actionRow?.InitialAbsoluteValue ?? 0}
        showApplyToChildren={actionRow?.HasChildren === true}
        forceApplyToChildren={
          actionRow?.HasChildren === true && actionRow?.IsReplaceable === false
        }
        isLoading={setInitialMetricMutation.isPending}
        onClose={handleCloseSetInitialMetricModal}
        onConfirm={handleSetInitialMetricConfirm}
      />

      {/* Correct Dates Modal */}
      <CorrectDatesModal
        open={correctDatesModalOpen}
        partId={correctDatesPartId}
        partName={correctDatesPartName}
        onClose={handleCloseCorrectDatesModal}
        onSave={handleCorrectDatesSave}
      />

      {/* Set Roof Status Modal (online/offline confirmation) */}
      <SetEquipmentStatusModal
        open={roofStatusModalOpen}
        equipmentName={
          treeDataRef.current.find(
            (row) =>
              row.rowType === "equipment" &&
              row.EquipmentId === roofStatusModalEquipmentId,
          )?.EquipmentName ?? null
        }
        equipmentType="Roof"
        action={roofStatusModalAction}
        partId={roofStatusModalEquipmentId}
        currentOnlineEquipmentName={
          roofStatusModalAction === "online"
            ? (treeDataRef.current.find(
                (row) =>
                  row.rowType === "equipment" &&
                  row.Status === "Online" &&
                  row.EquipmentId !== roofStatusModalEquipmentId,
              )?.EquipmentName ?? null)
            : null
        }
        occupantPartId={
          roofStatusModalAction === "online"
            ? (treeDataRef.current.find(
                (row) =>
                  row.rowType === "equipment" &&
                  row.Status === "Online" &&
                  row.EquipmentId !== roofStatusModalEquipmentId,
              )?.EquipmentId ?? null)
            : null
        }
        locationLastTimestamp={
          roofStatusModalAction === "online"
            ? (treeDataRef.current.find(
                (row) =>
                  row.rowType === "equipment" &&
                  row.Status === "Online" &&
                  row.EquipmentId !== roofStatusModalEquipmentId,
              )?.InstalledDate ?? null)
            : null
        }
        isLoading={setOnlineMutation.isPending || setOfflineMutation.isPending}
        onClose={handleCloseRoofStatusModal}
        onConfirm={handleRoofStatusConfirm}
      />

      {/* Delete Spare Part Confirmation Modal */}
      <DeleteConfirmModal
        open={deleteModalOpen}
        title="Delete Spare Part"
        message={`Are you sure you want to delete "${
          [
            partToDelete?.PartInfo?.SerialNumber,
            partToDelete?.PartInfo?.Make,
            partToDelete?.PartInfo?.Model,
          ]
            .filter(Boolean)
            .join(" ") || "this part"
        }"? This action cannot be undone.`}
        isLoading={deleteSpareMutation.isPending}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteSparePartConfirm}
      />

      {/* No Permission Modal */}
      <NoPermissionModal
        open={noPermissionModalOpen}
        actionName={noPermissionAction}
        onClose={() => setNoPermissionModalOpen(false)}
      />

      {/* Error Modal for mutation/query errors */}
      <ErrorModal error={errorMessage} onHide={() => setErrorMessage("")} />
    </>
  );
};
